import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

type Stats = {
  total: number;
  published: number;
  failed: number;
  processing: number;
};

export default function AnalyticsPage() {
  const [stats, setStats] = useState<Record<string, Stats>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/analytics`)
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          setError(data.error);
        } else {
          setStats(data.stats || {});
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const getProviderColor = (provider: string) => {
    return provider === 'facebook' ? '#1877F2' : '#010101';
  };

  const calculateSuccessRate = (stat: Stats) => {
    const finished = stat.published + stat.failed;
    if (finished === 0) return 0;
    return Math.round((stat.published / finished) * 100);
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ margin: '0 0 1.5rem', fontSize: '1.75rem', fontWeight: 600 }}>Analytics</h1>
      <p style={{ color: '#555', marginBottom: '2rem' }}>
        Overview of your cross-posting performance based on local operational data.
      </p>

      {loading && <div style={{ color: '#666' }}>Loading analytics...</div>}
      
      {error && (
        <div style={{ color: '#c62828', padding: '1rem', backgroundColor: '#ffebee', borderRadius: '6px' }}>
          Error: {error}
        </div>
      )}

      {!loading && !error && Object.keys(stats).length === 0 && (
        <div style={{ color: '#666' }}>No analytics data available yet. Start publishing to see metrics here.</div>
      )}

      {!loading && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {Object.entries(stats).map(([provider, stat]) => (
            <div key={provider} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1.5rem', backgroundColor: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <span style={{ 
                  fontWeight: 600, 
                  color: '#fff', 
                  backgroundColor: getProviderColor(provider),
                  padding: '0.35rem 0.75rem',
                  borderRadius: '4px',
                  textTransform: 'capitalize',
                  fontSize: '1rem'
                }}>
                  {provider}
                </span>
                <span style={{ color: '#666', fontWeight: 500 }}>
                  {calculateSuccessRate(stat)}% Success Rate
                </span>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 120px', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#333' }}>{stat.total}</div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem', textTransform: 'uppercase' }}>Total Attempts</div>
                </div>
                <div style={{ flex: '1 1 120px', padding: '1rem', backgroundColor: '#e8f5e9', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2e7d32' }}>{stat.published}</div>
                  <div style={{ fontSize: '0.85rem', color: '#2e7d32', marginTop: '0.25rem', textTransform: 'uppercase' }}>Published</div>
                </div>
                <div style={{ flex: '1 1 120px', padding: '1rem', backgroundColor: '#ffebee', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#c62828' }}>{stat.failed}</div>
                  <div style={{ fontSize: '0.85rem', color: '#c62828', marginTop: '0.25rem', textTransform: 'uppercase' }}>Failed</div>
                </div>
                <div style={{ flex: '1 1 120px', padding: '1rem', backgroundColor: '#e3f2fd', borderRadius: '6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1565c0' }}>{stat.processing}</div>
                  <div style={{ fontSize: '0.85rem', color: '#1565c0', marginTop: '0.25rem', textTransform: 'uppercase' }}>Processing</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}